import React, { useState, useEffect, useRef } from 'react';
import { FiMapPin, FiUsers, FiPlayCircle, FiSquare, FiClock, FiAlertCircle, FiRefreshCw, FiCheckCircle, FiEdit3, FiSearch } from 'react-icons/fi';
import api from '../../utils/api';
import '../../styles/mobileResponsive.css';

const font = "'Outfit', sans-serif";
const C = { text: '#1a0020', sub: '#64748b', border: '#e9d5ff', bg: '#fdf4ff', white: '#fff', accent: '#a855f7' };

const InvigilatorDashboard = () => {
    const [assignments, setAssignments] = useState([]);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [seats, setSeats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');
    const [msgType, setMsgType] = useState('');
    const [halting, setHalting] = useState(false);

    // ── NEW: Submission tracking state ──
    const [submissionStatuses, setSubmissionStatuses] = useState([]);
    const [submittedCount, setSubmittedCount] = useState(0);
    const [inProgressCount, setInProgressCount] = useState(0);
    const [restartingStudent, setRestartingStudent] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterType, setFilterType] = useState('all'); // 'all', 'submitted', 'in_progress'
    const [timeLeft, setTimeLeft] = useState(null);
    const pollRef = useRef(null);

    const load = async () => {
        try {
            const r = await api.get('/api/invigilator/my-assignments');
            setAssignments(r.data.assignments || []);

            // If an assignment is selected, refresh its data
            if (selectedAssignment) {
                const refreshed = r.data.assignments.find(a => a.id === selectedAssignment.id);
                if (refreshed) {
                    setSelectedAssignment(refreshed);
                    const sr = await api.get(`/api/invigilator/assignment/${refreshed.id}/seats`);
                    setSeats(sr.data.seats);
                }
            }
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    // ── NEW: Poll submission status every 10 seconds when exam is LIVE ──
    const fetchSubmissionStatus = async (assignmentId) => {
        try {
            const r = await api.get(`/api/invigilator/assignment/${assignmentId}/submission-status?t=${Date.now()}`);
            setSubmissionStatuses(r.data.statuses || []);
            setSubmittedCount(r.data.submitted || 0);
            setInProgressCount(r.data.inProgress || 0);
        } catch (e) { console.error('Status poll failed:', e); }
    };

    useEffect(() => {
        if (!selectedAssignment) return;
        // Fetch immediately
        fetchSubmissionStatus(selectedAssignment.id);

        // Poll every 10 seconds
        pollRef.current = setInterval(() => {
            fetchSubmissionStatus(selectedAssignment.id);
        }, 10000);

        return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }, [selectedAssignment?.id]);

    useEffect(() => {
        if (!selectedAssignment || selectedAssignment.status !== 'active') {
            setTimeLeft(null);
            return;
        }

        const calcTime = () => {
            if (!selectedAssignment.exam_date || !selectedAssignment.exam_time) return null;
            const timeParts = String(selectedAssignment.exam_time).split(/\s*-\s*/);
            if (timeParts.length < 2) return null;
            
            const dateStr = selectedAssignment.exam_date.slice(0, 10);
            let endStr = timeParts[1];
            
            let isPM = endStr.toUpperCase().includes('PM');
            let isAM = endStr.toUpperCase().includes('AM');
            endStr = endStr.replace(/AM|PM/gi, '').trim();
            
            let [hrs, mins] = endStr.split(':').map(Number);
            if (isPM && hrs < 12) hrs += 12;
            if (isAM && hrs === 12) hrs = 0;
            
            const endTime = new Date(`${dateStr}T${String(hrs).padStart(2, '0')}:${String(mins || 0).padStart(2, '0')}:00`);
            const now = new Date();
            const diff = endTime - now;
            
            if (diff <= 0) return "Time's up!";
            
            const h = Math.floor(diff / (1000 * 60 * 60));
            const m = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const s = Math.floor((diff % (1000 * 60)) / 1000);
            
            if (h > 0) return `${h}h ${m}m ${s}s`;
            return `${m}m ${s}s`;
        };

        setTimeLeft(calcTime());
        const t = setInterval(() => {
            setTimeLeft(calcTime());
        }, 1000);
        return () => clearInterval(t);
    }, [selectedAssignment]);

    const selectHall = async (hall) => {
        setLoading(true);
        setSearchQuery('');
        try {
            const sr = await api.get(`/api/invigilator/assignment/${hall.id}/seats`);
            setSeats(sr.data.seats);
            setSelectedAssignment(hall);
            setMsg('');
        } catch (e) {
            console.error(e);
            alert('Failed to load hall details.');
        } finally { 
            setLoading(false); 
            setFilterType('all');
        }
    };

    const startHall = async () => {
        const isRestart = selectedAssignment.status === 'ended';
        const confirmMsg = isRestart 
            ? 'Restart exam for this hall? Students will be able to enter again (though auto-submitted students will still need individual restarts).'
            : 'Start exam for your hall? Students will immediately see the question paper.';
            
        if (!window.confirm(confirmMsg)) return;
        setHalting(true); setMsg('');
        try {
            await api.put(`/api/invigilator/assignment/${selectedAssignment.id}/start`);
            setMsg('✅ Hall exam started! Students can now see the question paper.'); setMsgType('success');
            load();
        } catch (err) { setMsg(err.response?.data?.message || 'Failed to start'); setMsgType('error'); }
        finally { setHalting(false); }
    };

    const endHall = async () => {
        if (!window.confirm('End exam for your hall? This cannot be undone.')) return;
        setHalting(true);
        try {
            await api.put(`/api/invigilator/assignment/${selectedAssignment.id}/end`);
            setMsg('Hall exam ended.'); setMsgType('info');
            load();
        } catch (err) { setMsg('Failed to end hall'); setMsgType('error'); }
        finally { setHalting(false); }
    };

    // ── NEW: Restart a student's exam ──
    const restartStudentExam = async (studentUsername, studentName) => {
        if (!window.confirm(`Restart exam for "${studentName}" (${studentUsername})?\n\nTheir answers will be KEPT. They can continue from where they left off.`)) return;
        setRestartingStudent(studentUsername);
        try {
            await api.post('/api/invigilator/restart-student-exam', {
                student_username: studentUsername,
                assignment_id: selectedAssignment.id
            });
            setMsg(`✅ Exam restarted for ${studentName}. They can now re-enter.`); setMsgType('success');
            
            // Optimistically update UI to "Writing"
            setSubmissionStatuses(prev => prev.map(s => {
                const sid = (s.student_username || '').trim();
                const rid = (s.roll_number || '').trim();
                const target = (studentUsername || '').trim();
                
                if (sid === target || rid === target) {
                    return { ...s, status: 'in_progress', submitted_at: null, is_auto_submitted: false };
                }
                return s;
            }));

            // Refresh status from server after a short delay
            setTimeout(() => fetchSubmissionStatus(selectedAssignment.id), 2000);
        } catch (err) {
            setMsg(err.response?.data?.message || 'Failed to restart exam'); setMsgType('error');
        } finally { setRestartingStudent(null); }
    };

    if (loading && assignments.length === 0) return <div style={{ textAlign: 'center', padding: 60, color: C.sub, fontFamily: font }}>Loading your assignments...</div>;

    // ─── LIST VIEW ───
    if (!selectedAssignment) return (
        <div style={{ fontFamily: font }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                    <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: 0 }}>Assigned Halls</h1>
                    <p style={{ color: C.sub, fontSize: 13, margin: 0 }}>Select a hall to manage proctoring</p>
                </div>
                <button onClick={load} style={{ background: 'none', border: `1px solid ${C.border}`, padding: '6px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer', color: C.sub }}>Refresh</button>
            </div>

            {assignments.length === 0 ? (
                <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, padding: '40px 20px', textAlign: 'center' }}>
                    <FiAlertCircle size={48} color={C.accent} style={{ opacity: 0.3, marginBottom: 16 }} />
                    <h3 style={{ color: C.text, fontWeight: 800 }}>No assignments found</h3>
                    <p style={{ color: C.sub, fontSize: 14 }}>You are not currently assigned to any examination halls.</p>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                    {assignments.map(a => (
                        <div key={a.id} onClick={() => selectHall(a)} className="card-hover" style={{
                            background: C.white, borderRadius: 18, border: `1.5px solid ${C.border}`,
                            padding: 20, cursor: 'pointer', position: 'relative', overflow: 'hidden',
                            boxShadow: '0 4px 12px rgba(168,85,247,0.05)'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                                <div style={{ fontSize: 24, fontWeight: 900, color: C.accent }}>Hall {a.hall_number}</div>
                                <div style={{
                                    padding: '4px 10px', borderRadius: 8, fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
                                    background: a.status === 'active' ? '#d1fae5' : a.status === 'ended' ? '#f1f5f9' : '#fef3c7',
                                    color: a.status === 'active' ? '#059669' : a.status === 'ended' ? '#64748b' : '#d97706'
                                }}>
                                    {a.status}
                                </div>
                            </div>
                            <div style={{ fontWeight: 800, fontSize: 14, color: C.text, marginBottom: 4 }}>{a.subject_name}</div>
                            <div style={{ fontSize: 12, color: C.sub, marginBottom: 12 }}>{a.subject_code} • {a.batch_name}</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.sub }}>
                                <FiClock size={14} /> {a.exam_time} | {a.exam_date?.slice(0, 10)}
                            </div>
                            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 11, color: C.sub }}>{a.building}</span>
                                <span style={{ fontSize: 11, fontWeight: 700, color: C.accent }}>Manage Hall →</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    // ─── DETAIL VIEW ───
    const isWaiting = selectedAssignment.status === 'waiting';
    const isActive = selectedAssignment.status === 'active';
    const isEnded = selectedAssignment.status === 'ended';
    const sessionNotStarted = selectedAssignment.session_status !== 'active';

    // Build a map of student_username -> status for quick lookups from the polling data
    const statusMap = {};
    submissionStatuses.forEach(s => {
        if (s.student_username) statusMap[s.student_username] = s;
        if (s.roll_number) statusMap[s.roll_number] = s;
    });

    // Only count seats that have an actual student assigned
    const seatsWithStudents = seats.filter(s => s.student_name && s.student_name.trim() !== '');

    // Filter seats by search AND status
    const filteredSeats = seats.filter(s => {
        const isEmpty = !s.student_name || s.student_name.trim() === '';
        
        // 1. Filter by search query
        const q = searchQuery.toLowerCase();
        const matchesSearch = !searchQuery || 
            (s.student_name || '').toLowerCase().includes(q) ||
            (s.roll_number || '').toLowerCase().includes(q) ||
            (s.anonymous_id || '').toLowerCase().includes(q) ||
            String(s.seat_number).includes(q);

        if (!matchesSearch) return false;

        // 2. Filter by status type
        if (filterType === 'all') return true;
        if (isEmpty) return false; // Hide empty seats in status filters

        const statusData = statusMap[s.student_username] || statusMap[s.roll_number];
        const isSubmitted = statusData?.status === 'submitted';

        if (filterType === 'submitted') return isSubmitted;
        if (filterType === 'in_progress') return !isSubmitted;

        return true;
    });

    return (
        <div style={{ fontFamily: font }}>
            <button onClick={() => { setSelectedAssignment(null); setSubmissionStatuses([]); setSearchQuery(''); }} style={{ background: 'none', border: 'none', color: C.accent, fontWeight: 800, fontSize: 13, marginBottom: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                ← Back to Dashboard
            </button>

            <h1 style={{ fontSize: 22, fontWeight: 900, color: C.text, margin: '0 0 6px' }}>Hall {selectedAssignment.hall_number}</h1>
            <p style={{ color: C.sub, fontSize: 14, margin: '0 0 20px' }}>{selectedAssignment.building} • {selectedAssignment.subject_name}</p>

            {msg && (
                <div style={{ padding: '12px 16px', background: msgType === 'success' ? '#d1fae5' : msgType === 'error' ? '#fee2e2' : '#e0f2fe', color: msgType === 'success' ? '#065f46' : msgType === 'error' ? '#991b1b' : '#0369a1', borderRadius: 12, marginBottom: 20, fontWeight: 700, fontSize: 13 }}>{msg}</div>
            )}

            {/* Room Info Card */}
            <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, padding: '20px', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div style={{ padding: '4px 12px', borderRadius: 20, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, background: isActive ? '#d1fae5' : isEnded ? '#f1f5f9' : '#fef3c7', color: isActive ? '#059669' : isEnded ? '#64748b' : '#d97706' }}>
                        {isActive ? '🔴 EXAM LIVE' : isEnded ? 'Ended' : 'Waiting to Start'}
                    </div>
                    {isActive && timeLeft && (
                        <div style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 800, background: '#fee2e2', color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6 }}>
                            <FiClock size={12} /> Time Left: {timeLeft}
                        </div>
                    )}
                    {sessionNotStarted && !isEnded && <span style={{ fontSize: 12, color: '#d97706', fontWeight: 600 }}>⚠️ Waiting for Exam Controller to start</span>}
                </div>

                <div className="resp-info-grid" style={{ marginBottom: 16 }}>
                    {[
                        { icon: <FiMapPin />, label: 'Hall', val: selectedAssignment.hall_number },
                        { icon: <FiUsers />, label: 'Capacity', val: `${seatsWithStudents.length}/${selectedAssignment.capacity} seats` },
                        { icon: <FiClock />, label: 'Exam Time', val: `${selectedAssignment.exam_time || '—'} | ${selectedAssignment.exam_date?.slice(0, 10)}` }
                    ].map((item, i) => (
                        <div key={i}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>{item.icon} {item.label}</div>
                            <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{item.val}</div>
                        </div>
                    ))}
                </div>

                <div style={{ padding: '12px 16px', background: C.bg, borderRadius: 12, marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Exam Subject</div>
                    <div style={{ fontSize: 15, fontWeight: 900, color: C.text }}>{selectedAssignment.subject_name} <span style={{ fontSize: 12, color: C.sub, fontWeight: 600 }}>({selectedAssignment.subject_code})</span></div>
                    <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>{selectedAssignment.department} • Sem {selectedAssignment.semester} • {selectedAssignment.batch_name}</div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {isWaiting && (
                        <button onClick={startHall} disabled={halting || sessionNotStarted} style={{ padding: '14px 24px', borderRadius: 14, border: 'none', background: sessionNotStarted ? '#f1f5f9' : 'linear-gradient(135deg,#4ade80,#16a34a)', color: sessionNotStarted ? C.sub : '#fff', fontWeight: 900, fontSize: 14, cursor: sessionNotStarted ? 'not-allowed' : 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', boxShadow: sessionNotStarted ? 'none' : '0 4px 14px rgba(22,163,74,0.35)', width: '100%' }}>
                            <FiPlayCircle size={18} /> {halting ? 'Starting...' : 'Start Hall Exam'}
                        </button>
                    )}
                    {isActive && (
                        <button onClick={endHall} disabled={halting} style={{ padding: '14px 24px', borderRadius: 14, border: 'none', background: 'linear-gradient(135deg,#f87171,#dc2626)', color: '#fff', fontWeight: 900, fontSize: 14, cursor: 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center', boxShadow: '0 4px 14px rgba(220,38,38,0.3)', width: '100%' }}>
                            <FiSquare size={18} /> {halting ? 'Ending...' : 'End Hall Exam'}
                        </button>
                    )}
                    {isEnded && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ padding: '12px 20px', borderRadius: 12, background: '#f1f5f9', color: C.sub, fontWeight: 700, fontSize: 13, textAlign: 'center' }}>Exam Completed</div>
                            <button 
                                onClick={startHall} 
                                disabled={halting}
                                style={{ 
                                    padding: '10px 20px', borderRadius: 12, border: '1.5px solid #4ade80', 
                                    background: '#f0fdf4', color: '#16a34a', fontWeight: 800, fontSize: 12, 
                                    cursor: 'pointer', fontFamily: font, display: 'flex', alignItems: 'center', 
                                    gap: 6, justifyContent: 'center', transition: 'all 0.2s' 
                                }}
                            >
                                <FiRefreshCw size={14} className={halting ? 'spin' : ''} /> 
                                {halting ? 'Restarting...' : 'Restart Hall Exam'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* ── NEW: Live Submission Summary Bar ── */}
            {(isActive || isEnded) && submissionStatuses.length > 0 && (
                <div style={{
                    background: C.white, borderRadius: 16, border: `1.5px solid ${C.border}`,
                    padding: '16px 20px', marginBottom: 20,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <button 
                            onClick={() => setFilterType(filterType === 'submitted' ? 'all' : 'submitted')}
                            style={{ 
                                display: 'flex', alignItems: 'center', gap: 8, background: filterType === 'submitted' ? '#dcfce7' : 'none', 
                                border: `1px solid ${filterType === 'submitted' ? '#22c55e' : '#e2e8f0'}`, 
                                padding: '6px 12px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s'
                            }}
                        >
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#22c55e' }} />
                            <span style={{ fontSize: 13, fontWeight: 800, color: filterType === 'submitted' ? '#16a34a' : C.text }}>Submitted: {submittedCount}</span>
                        </button>
                        <button 
                            onClick={() => setFilterType(filterType === 'in_progress' ? 'all' : 'in_progress')}
                            style={{ 
                                display: 'flex', alignItems: 'center', gap: 8, background: filterType === 'in_progress' ? '#fef9c3' : 'none', 
                                border: `1px solid ${filterType === 'in_progress' ? '#eab308' : '#e2e8f0'}`, 
                                padding: '6px 12px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.2s'
                            }}
                        >
                            <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#eab308' }} />
                            <span style={{ fontSize: 13, fontWeight: 800, color: filterType === 'in_progress' ? '#a16207' : C.text }}>In Progress: {inProgressCount}</span>
                        </button>
                    </div>
                    {filterType !== 'all' && (
                        <button 
                            onClick={() => setFilterType('all')}
                            style={{ background: 'none', border: 'none', color: C.accent, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                        >
                            Clear Filter
                        </button>
                    )}
                    <div style={{ fontSize: 13, color: C.sub, fontWeight: 600 }}>
                        Total: {seatsWithStudents.length} students • Auto-refreshing every 10s
                    </div>
                </div>
            )}

            {/* Seat List with Status */}
            <div style={{ background: C.white, borderRadius: 20, border: `1.5px solid ${C.border}`, overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', background: C.bg, borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                    <span style={{ fontWeight: 800, fontSize: 14, color: C.text }}>Seat Allocation — {seatsWithStudents.length} students</span>
                    {seatsWithStudents.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: C.white, borderRadius: 8, border: `1px solid ${C.border}`, padding: '4px 10px', maxWidth: 260 }}>
                            <FiSearch size={13} color={C.sub} />
                            <input
                                type="text"
                                placeholder="Search student..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                style={{ border: 'none', outline: 'none', fontSize: 12, fontFamily: font, flex: 1, color: C.text, background: 'transparent' }}
                            />
                            {searchQuery && (
                                <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.sub, fontSize: 14, padding: 0 }}>×</button>
                            )}
                        </div>
                    )}
                </div>
                {seats.length === 0 ? (
                    <div style={{ padding: 30, textAlign: 'center', color: C.sub, fontSize: 13 }}>No seats allocated yet.</div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, padding: 16 }}>
                        {filteredSeats.map(s => {
                            const isEmpty = !s.student_name || s.student_name.trim() === '';
                            const statusData = statusMap[s.student_username] || statusMap[s.roll_number];
                            const isSubmitted = statusData?.status === 'submitted';
                            const isWaitingOrNotStarted = isWaiting || !isActive;
                            
                            const statusLabel = isSubmitted 
                                ? (statusData?.is_auto_submitted ? 'Auto Submitted ✅' : 'Submitted ✅') 
                                : (isEnded ? 'Not Submitted ❌' : (isWaitingOrNotStarted ? 'Waiting... ⏳' : 'Writing... ✏️'));
                            const borderColor = isEmpty ? '#e2e8f0' : (isSubmitted ? '#22c55e' : (isEnded ? '#ef4444' : (isWaitingOrNotStarted ? '#cbd5e1' : '#eab308')));
                            const statusBg = isSubmitted ? '#dcfce7' : (isEnded ? '#fee2e2' : (isWaitingOrNotStarted ? '#f8fafc' : '#fef9c3'));
                            const statusTextColor = isSubmitted ? '#15803d' : (isEnded ? '#991b1b' : (isWaitingOrNotStarted ? '#64748b' : '#a16207'));

                            return (
                                <div key={s.id} style={{
                                    background: C.bg, borderRadius: 12,
                                    border: `2.5px solid ${borderColor}`,
                                    padding: '12px 14px',
                                    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
                                    boxShadow: isEmpty ? 'none' : (isSubmitted ? '0 2px 8px rgba(34,197,94,0.15)' : '0 2px 8px rgba(234,179,8,0.1)'),
                                    opacity: isEmpty ? 0.6 : 1
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                                        <div style={{ fontSize: 18, fontWeight: 900, color: isEmpty ? '#94a3b8' : C.accent }}>Seat {s.seat_number}</div>
                                        {/* Status Badge */}
                                        {!isEmpty && (
                                            <div style={{
                                                padding: '2px 8px', borderRadius: 20, fontSize: 9, fontWeight: 800,
                                                background: statusBg, color: statusTextColor,
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {statusLabel}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginTop: 4 }}>{s.student_name}</div>
                                    <div style={{ fontSize: 11, color: C.sub }}>{s.roll_number}</div>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: '#7c3aed', background: '#f3e8ff', padding: '2px 8px', borderRadius: 20, display: 'inline-block', marginTop: 6 }}>{s.anonymous_id}</div>

                                    {s.uploaded_file_path && <div style={{ fontSize: 10, color: '#059669', fontWeight: 700, marginTop: 6 }}>✅ Answer uploaded</div>}

                                    {/* Restart button — only for submitted students AND when exam is ACTIVE */}
                                    {isSubmitted && isActive && (
                                        <button
                                            onClick={() => restartStudentExam(s.student_username || s.roll_number, s.student_name)}
                                            disabled={restartingStudent === (s.student_username || s.roll_number)}
                                            style={{
                                                marginTop: 8, width: '100%', padding: '6px 10px', borderRadius: 8,
                                                border: '1px solid #c084fc', background: '#faf5ff', color: '#7c3aed',
                                                fontSize: 11, fontWeight: 800, cursor: 'pointer', fontFamily: font,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                                                opacity: restartingStudent === (s.student_username || s.roll_number) ? 0.5 : 1
                                            }}
                                        >
                                            <FiRefreshCw size={11} /> {restartingStudent === (s.student_username || s.roll_number) ? 'Restarting...' : 'Restart Exam'}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvigilatorDashboard;
